import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Award, Truck, Clock, Star, ChevronRight, Wrench, Hammer, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Banner, BannerGrid } from '../components/Banner';
import { brands as mockBrands, testimonials } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const Home = () => {
  const [brands, setBrands] = useState(mockBrands);
  const [categories, setCategories] = useState([]);
  const [siteSettings, setSiteSettings] = useState({
    // Seção Parceiro Destaque
    partner_section_logo: 'https://cdn.worldvectorlogo.com/logos/stihl.svg',
    partner_section_title: 'Distribuidor Autorizado',
    partner_section_brand: 'STIHL',
    partner_section_description: 'A Casa das Ferramentas é distribuidor autorizado STIHL em Canaã dos Carajás. Oferecemos toda a linha de motosserras, roçadeiras, sopradores e equipamentos profissionais com garantia oficial de fábrica.',
    partner_section_indicator1_title: '100%',
    partner_section_indicator1_subtitle: 'Produtos Originais',
    partner_section_indicator2_title: 'Garantia',
    partner_section_indicator2_subtitle: 'Oficial de Fábrica',
    partner_section_button_text: 'Ver Produtos STIHL',
    partner_section_button_link: '/catalogo?marca=STIHL',
    // Trust indicators
    hero_indicator1_title: 'STIHL',
    hero_indicator1_subtitle: 'Distribuidor Oficial',
    hero_indicator1_logo: null,
    hero_indicator2_title: '8+',
    hero_indicator2_subtitle: 'Marcas Líderes',
    hero_indicator3_title: '100%',
    hero_indicator3_subtitle: 'Produtos Originais',
    hero_indicator4_title: 'Garantia',
    hero_indicator4_subtitle: 'De Fábrica'
  });

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/brands`);
        if (response.data && response.data.length > 0) {
          setBrands(response.data);
        }
      } catch (error) {
        // Use mock data
      }
    };
    
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/settings`);
        if (response.data) {
          setSiteSettings(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        // Use default settings
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/categories`);
        if (response.data && response.data.length > 0) {
          setCategories(response.data);
        }
      } catch (error) {
        // Use empty array
      }
    };
    
    fetchBrands();
    fetchSettings();
    fetchCategories();

    // Track page view
    const trackView = async () => {
      try {
        await axios.post(`${BACKEND_URL}/api/track/view?page=home`);
      } catch (e) {}
    };
    trackView();
  }, []);

  const featuredBrands = brands.filter(b => b.featured);
  const otherBrands = brands.filter(b => !b.featured);

  return (
    <div className="min-h-screen">
      {/* Hero Section - Melhorado */}
      <section className="relative overflow-hidden min-h-[90vh] flex items-center">
        {/* Background Image with Overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center z-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1693823462803-8921d3f5ea2c?w=1920')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-950/95 via-blue-900/90 to-red-900/85"></div>
          {/* Animated Shapes */}
          <div className="absolute top-20 right-20 w-72 h-72 bg-red-600/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-6 py-3 mb-8 animate-fade-in">
              <Wrench className="text-yellow-400" size={20} />
              <span className="text-white font-medium">Distribuidor Autorizado em Canaã dos Carajás</span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-white mb-6 leading-tight tracking-tight animate-fade-in">
              Encontre a Ferramenta
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-sky-300 to-blue-400">
                Certa Para Você
              </span>
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl text-blue-100 mb-10 leading-relaxed max-w-3xl mx-auto animate-fade-in">
              Variedade e qualidade em máquinas e ferramentas das melhores marcas do mercado.
              <span className="hidden sm:inline"> Produtos originais com garantia de fábrica.</span>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12 animate-fade-in">
              <Link to="/catalogo">
                <Button size="lg" className="w-full sm:w-auto bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold text-base sm:text-lg px-8 sm:px-10 py-6 sm:py-7 shadow-2xl hover:shadow-red-500/50 hover:scale-105 transition-all">
                  Acessar Catálogo
                  <ArrowRight className="ml-2" size={22} />
                </Button>
              </Link>
              <Link to="/orcamento">
                <Button size="lg" className="w-full sm:w-auto bg-white/20 backdrop-blur-md border-2 border-white/50 text-white hover:bg-white hover:text-blue-900 font-bold text-base sm:text-lg px-8 sm:px-10 py-6 sm:py-7 hover:scale-105 transition-all">
                  Pedir Orçamento
                  <ChevronRight className="ml-2" size={22} />
                </Button>
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-6 max-w-4xl mx-auto animate-fade-in">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3 sm:p-4">
                {siteSettings.hero_indicator1_logo ? (
                  <img src={siteSettings.hero_indicator1_logo} alt={siteSettings.hero_indicator1_title} className="h-8 sm:h-10 w-auto object-contain mb-1 brightness-0 invert" />
                ) : (
                  <div className="text-2xl sm:text-3xl font-black text-white mb-1">{siteSettings.hero_indicator1_title}</div>
                )}
                <div className="text-xs sm:text-sm text-blue-200">{siteSettings.hero_indicator1_subtitle}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3 sm:p-4">
                <div className="text-2xl sm:text-3xl font-black text-white mb-1">{siteSettings.hero_indicator2_title}</div>
                <div className="text-xs sm:text-sm text-blue-200">{siteSettings.hero_indicator2_subtitle}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3 sm:p-4">
                <div className="text-2xl sm:text-3xl font-black text-white mb-1">{siteSettings.hero_indicator3_title}</div>
                <div className="text-xs sm:text-sm text-blue-200">{siteSettings.hero_indicator3_subtitle}</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3 sm:p-4">
                <div className="text-2xl sm:text-3xl font-black text-white mb-1">{siteSettings.hero_indicator4_title}</div>
                <div className="text-xs sm:text-sm text-blue-200">{siteSettings.hero_indicator4_subtitle}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-10 hidden md:block">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-white/70 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Brands Section - Melhorado */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4">
          {/* Banner Promocional */}
          <Banner position="home_hero" className="mb-12" />
          
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-900 rounded-full px-4 py-2 mb-4 font-semibold text-sm">
              <Award size={18} />
              Marcas Parceiras
            </div>
            <h2 className="text-3xl md:text-5xl font-black text-gray-900 mb-4">
              Trabalhamos com os 
              <span className="text-red-600"> Melhores do Mercado</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Produtos originais com garantia de fábrica
            </p>
          </div>

          {/* Featured Brands - Dynamic */}
          <div className="grid md:grid-cols-2 gap-6 md:gap-8 mb-12 max-w-5xl mx-auto">
            {(featuredBrands.length > 0 ? featuredBrands : brands.slice(0, 2)).map((brand) => (
              <Link
                key={brand.id}
                to={`/catalogo?marca=${brand.name}`}
                className="group relative bg-white p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all hover:-translate-y-2 border-2 border-gray-100 hover:border-red-200 overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-500/5 to-blue-500/5 rounded-bl-full"></div>
                <div className="relative">
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-6 md:p-8 rounded-xl mb-4 flex items-center justify-center h-[100px] md:h-[120px]">
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      className="max-h-14 md:max-h-16 max-w-[180px] w-auto object-contain group-hover:scale-110 transition-transform"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-base md:text-lg font-bold text-gray-900 group-hover:text-red-600 transition-colors">
                      Ver Produtos {brand.name}
                    </span>
                    <ChevronRight className="text-gray-400 group-hover:text-red-600 group-hover:translate-x-1 transition-all" size={24} />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Other Brands - Grid Simétrico */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 md:gap-6 max-w-5xl mx-auto">
            {(featuredBrands.length > 0 ? otherBrands : brands.slice(2)).map((brand) => (
              <Link
                key={brand.id}
                to={`/catalogo?marca=${brand.name}`}
                className="group bg-white p-4 md:p-6 rounded-xl shadow-md hover:shadow-xl transition-all hover:-translate-y-1 border border-gray-100 hover:border-red-200 flex items-center justify-center h-[70px] md:h-[90px]"
              >
                <img
                  src={brand.logo}
                  alt={brand.name}
                  className="max-h-8 md:max-h-10 max-w-[80px] md:max-w-[100px] w-auto object-contain grayscale group-hover:grayscale-0 transition-all"
                />
              </Link>
            ))}
          </div>

          {/* Banner Meio */}
          <BannerGrid position="home_middle" columns={2} className="mt-12 max-w-5xl mx-auto" />
        </div>
      </section>

      {/* Categories Section - Melhorado */}
      <section className="py-20 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent"></div>
        
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-red-100 text-red-900 rounded-full px-4 py-2 mb-4 font-semibold text-sm">
              <Hammer size={18} />
              Nosso Catálogo
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Encontre Exatamente o Que
              <span className="text-red-600"> Você Precisa</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Organize sua busca por categoria e descubra as melhores ferramentas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {categories.length > 0 ? categories.map((category, index) => (
              <Link
                key={category.id}
                to={`/catalogo?cat=${category.id}`}
                className="group relative"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-2 border-gray-100 hover:border-red-300 bg-gradient-to-br from-white to-gray-50 group-hover:from-red-50 group-hover:to-orange-50">
                  <CardContent className="p-8 relative">
                    {/* Icon Background */}
                    <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-br from-red-100 to-blue-100 rounded-full opacity-20 group-hover:opacity-30 transition-opacity"></div>
                    
                    {/* Content */}
                    <div className="relative">
                      <div className="bg-gradient-to-br from-red-600 to-red-700 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all shadow-lg">
                        <Zap className="text-white" size={28} />
                      </div>
                      
                      <h3 className="text-2xl font-black text-gray-900 mb-2 group-hover:text-red-600 transition-colors">
                        {category.name}
                      </h3>
                      
                      <div className="flex items-center text-red-600 font-semibold group-hover:gap-2 transition-all">
                        <span>Ver produtos</span>
                        <ChevronRight className="group-hover:translate-x-1 transition-transform" size={20} />
                      </div>
                    </div>

                    {/* Hover Effect Line */}
                    <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-red-600 to-orange-600 w-0 group-hover:w-full transition-all duration-500"></div>
                  </CardContent>
                </Card>
              </Link>
            )) : (
              <div className="col-span-3 text-center py-12">
                <p className="text-gray-500">Carregando categorias...</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why Choose Us - Melhorado */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-900 rounded-full px-4 py-2 mb-4 font-semibold text-sm">
              <Shield size={18} />
              Nossos Diferenciais
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Por Que Escolher a
              <span className="text-red-600"> Casa das Ferramentas?</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            <Card className="text-center hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-red-200 group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-500/5 to-transparent rounded-bl-full"></div>
              <CardContent className="p-8 relative">
                <div className="bg-gradient-to-br from-red-600 to-red-700 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all shadow-xl">
                  <Shield className="text-white" size={36} />
                </div>
                <h3 className="text-xl font-black mb-3 text-gray-900 group-hover:text-red-600 transition-colors">Produtos Originais</h3>
                <p className="text-gray-600 leading-relaxed">Garantia de fábrica e qualidade certificada em todos os produtos</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-blue-200 group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/5 to-transparent rounded-bl-full"></div>
              <CardContent className="p-8 relative">
                <div className="bg-gradient-to-br from-blue-600 to-blue-700 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all shadow-xl">
                  <Award className="text-white" size={36} />
                </div>
                <h3 className="text-xl font-black mb-3 text-gray-900 group-hover:text-blue-600 transition-colors">Tradição Regional</h3>
                <p className="text-gray-600 leading-relaxed">Empresa consolidada em Canaã dos Carajás</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-green-200 group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/5 to-transparent rounded-bl-full"></div>
              <CardContent className="p-8 relative">
                <div className="bg-gradient-to-br from-green-600 to-green-700 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all shadow-xl">
                  <Truck className="text-white" size={36} />
                </div>
                <h3 className="text-xl font-black mb-3 text-gray-900 group-hover:text-green-600 transition-colors">Entrega Rápida</h3>
                <p className="text-gray-600 leading-relaxed">Atendimento ágil e logística eficiente em toda região</p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-2xl transition-all duration-300 border-2 border-gray-100 hover:border-yellow-200 group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-yellow-500/5 to-transparent rounded-bl-full"></div>
              <CardContent className="p-8 relative">
                <div className="bg-gradient-to-br from-yellow-600 to-yellow-700 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all shadow-xl">
                  <Clock className="text-white" size={36} />
                </div>
                <h3 className="text-xl font-black mb-3 text-gray-900 group-hover:text-yellow-600 transition-colors">Suporte Especializado</h3>
                <p className="text-gray-600 leading-relaxed">Equipe técnica preparada para auxiliar na escolha ideal</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Partner Section - Dinâmico */}
      <section className="py-20 relative overflow-hidden">
        {/* Background com imagem */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-600 via-gray-900 to-black opacity-95"></div>
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1683115099413-5b7d85c2950c?w=1920')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Logo Grande */}
            <div className="text-center md:text-left">
              <div className="bg-white/95 backdrop-blur-sm p-12 rounded-3xl shadow-2xl inline-block">
                <img 
                  src={siteSettings.partner_section_logo}
                  alt={`${siteSettings.partner_section_brand} - ${siteSettings.partner_section_title}`}
                  className="w-full max-w-md mx-auto"
                />
              </div>
            </div>

            {/* Conteúdo */}
            <div className="text-white">
              <h2 className="text-4xl md:text-5xl font-black mb-6 leading-tight">
                {siteSettings.partner_section_title}
                <span className="block text-orange-400">{siteSettings.partner_section_brand}</span>
              </h2>
              <p className="text-xl text-gray-200 mb-8 leading-relaxed">
                {siteSettings.partner_section_description}
              </p>
              
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6">
                  <div className="text-3xl font-black text-orange-400 mb-2">{siteSettings.partner_section_indicator1_title}</div>
                  <div className="text-sm">{siteSettings.partner_section_indicator1_subtitle}</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6">
                  <div className="text-3xl font-black text-orange-400 mb-2">{siteSettings.partner_section_indicator2_title}</div>
                  <div className="text-sm">{siteSettings.partner_section_indicator2_subtitle}</div>
                </div>
              </div>

              <Link to={siteSettings.partner_section_button_link}>
                <Button size="lg" className="bg-orange-600 hover:bg-orange-700 text-white font-black text-lg px-10 py-7 shadow-2xl hover:scale-105 transition-all">
                  {siteSettings.partner_section_button_text}
                  <ArrowRight className="ml-2" size={22} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Melhorado */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-600 via-red-700 to-orange-600"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgY3g9IjIwIiBjeT0iMjAiIHI9IjEiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iLjEiLz48L2c+PC9zdmc+')] opacity-20"></div>
        
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
              Pronto Para Encontrar a Ferramenta Perfeita?
            </h2>
            <p className="text-xl md:text-2xl mb-10 text-red-100 leading-relaxed">
              Fale com nossos especialistas e receba um orçamento personalizado sem compromisso
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contato">
                <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 font-black text-lg px-12 py-7 shadow-2xl hover:scale-105 transition-all">
                  Fale Conosco Agora
                  <ArrowRight className="ml-2" size={22} />
                </Button>
              </Link>
              <Link to="/catalogo">
                <Button size="lg" className="bg-white/20 backdrop-blur-md border-2 border-white text-white hover:bg-white hover:text-red-600 font-black text-lg px-12 py-7 hover:scale-105 transition-all">
                  Ver Catálogo
                </Button>
              </Link>
            </div>

            {/* Trust Line */}
            <div className="mt-12 flex flex-wrap items-center justify-center gap-6 md:gap-8 text-white/80 text-sm">
              <div className="flex items-center gap-2">
                <Shield size={18} />
                <span>Produtos Originais</span>
              </div>
              <div className="flex items-center gap-2">
                <Award size={18} />
                <span>Garantia de Fábrica</span>
              </div>
              <div className="flex items-center gap-2">
                <Truck size={18} />
                <span>Atendimento Regional</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
